import React, { Fragment, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDown, FolderPlus, Pencil, Trash2, Check, X, Folder, AlertTriangle } from "lucide-react";
import { useImageStudio } from "../../context/ImageStudioContext";

const GallerySwitcher: React.FC = () => {
  const {
    galleries,
    activeGallery,
    activeGalleryId,
    switchGallery,
    createGallery,
    renameGallery,
    deleteGallery,
    galleriesLoading,
  } = useImageStudio();

  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const startRename = () => {
    setDraftName(activeGallery?.name || "");
    setRenaming(true);
  };

  const commitRename = () => {
    const name = draftName.trim();
    if (activeGalleryId && name && name !== activeGallery?.name) {
      renameGallery(activeGalleryId, name);
    }
    setRenaming(false);
  };

  const confirmDelete = () => {
    if (!activeGalleryId) return;
    deleteGallery(activeGalleryId);
    setConfirmingDelete(false);
  };

  if (renaming) {
    return (
      <div className="flex items-center gap-1">
        <input
          autoFocus
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename();
            if (e.key === "Escape") setRenaming(false);
          }}
          className="w-44 rounded-md bg-surface-2 border border-border-default px-2 py-1 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-violet-500/60"
        />
        <button onClick={commitRename} title="Save" className="rounded p-1 text-violet-500 hover:bg-surface-2">
          <Check size={15} />
        </button>
        <button onClick={() => setRenaming(false)} title="Cancel" className="rounded p-1 text-subtle hover:bg-surface-2">
          <X size={15} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Menu as="div" className="relative">
        <Menu.Button className="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-surface-2">
          <Folder size={15} className="text-violet-500" />
          <span className="max-w-[180px] truncate">
            {galleriesLoading ? "Loading…" : activeGallery?.name || "No gallery"}
          </span>
          <ChevronDown size={14} className="text-subtle" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute left-0 z-20 mt-1 max-h-80 w-64 overflow-y-auto rounded-lg border border-border-default bg-surface-overlay py-1 shadow-lg focus:outline-none">
            <div className="px-2 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-subtle">
              Galleries
            </div>
            {galleries.map((p) => (
              <Menu.Item key={p.gallery_id}>
                {({ active }) => (
                  <button
                    onClick={() => switchGallery(p.gallery_id)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-sm ${
                      active ? "bg-surface-2" : ""
                    } ${p.gallery_id === activeGalleryId ? "text-primary" : "text-muted"}`}
                  >
                    <span className="truncate">{p.name}</span>
                    <span className="shrink-0 text-xs text-subtle">{p.asset_count}</span>
                  </button>
                )}
              </Menu.Item>
            ))}
            <div className="my-1 border-t border-border-default" />
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => createGallery()}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-muted ${
                    active ? "bg-surface-2 text-primary" : ""
                  }`}
                >
                  <FolderPlus size={15} /> New gallery
                </button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>

      {activeGallery && (
        <>
          <button onClick={startRename} title="Rename gallery" className="rounded p-1 text-subtle hover:bg-surface-2 hover:text-primary">
            <Pencil size={14} />
          </button>
          <button onClick={() => setConfirmingDelete(true)} title="Delete gallery" className="rounded p-1 text-subtle hover:bg-surface-2 hover:text-red-500">
            <Trash2 size={14} />
          </button>
        </>
      )}

      {confirmingDelete && activeGallery && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setConfirmingDelete(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-sm rounded-lg border border-border-default bg-surface-overlay shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 p-5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-red-500">
                <AlertTriangle size={20} />
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-primary">Delete gallery</h2>
                <p className="mt-1 text-sm text-muted">
                  Delete <span className="font-medium text-primary">“{activeGallery.name}”</span> and all of its assets? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-border-default px-5 py-3">
              <button
                onClick={() => setConfirmingDelete(false)}
                className="rounded-md px-3 py-1.5 text-sm font-medium text-muted hover:bg-surface-2 hover:text-primary"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GallerySwitcher;
